import React, { ReactNode, useEffect, useMemo } from 'react';
import {
    ThemeOptions,
    ThemeProvider,
    createTheme,
    alpha,
    lighten,
    darken,
} from '@mui/material/styles';
import { useAppSelector } from '../common/hook';
import { BASE_COLOR } from '../common/constants';
import {
    selectPrimaryColor,
    selectLabelTextColor,
    selectUserLogo,
} from '../common/theme-selectors';

/** Kiểm tra chuỗi có phải CSS color hợp lệ không (hex / rgb / hsl). */
const isValidColor = (color: string): boolean =>
    /^#[0-9A-Fa-f]{3,8}$/.test(color) || /^(rgb|hsl)a?\(/.test(color);

/** Trả về color nếu hợp lệ, ngược lại fallback BASE_COLOR để tránh crash MUI utils. */
const safeColor = (color: string): string =>
    isValidColor(color) ? color : BASE_COLOR;

export const buildTheme = (
    primaryColor: string,
    contrastText = '#fff'
): ThemeOptions =>
    createTheme({
        typography: {
            fontFamily: 'Inter, sans-serif',
        },
        palette: {
            primary: {
                main: safeColor(primaryColor),
                contrastText,
            },
            secondary: {
                main: '#EFF1FF',
            },
            warning: {
                main: '#ff7a19',
                light: '#fff6e5',
            },
            info: {
                main: '#313af5',
                light: '#39d0ff',
            },
        },
    });

const derivedVars = (primary: string): Record<string, string> => {
    const light = lighten(primary, 0.72);
    return {
        '--color-primary': primary,
        '--color-primary-dark': darken(primary, 0.15),
        '--color-primary-mid': lighten(primary, 0.35),
        '--color-primary-light': light,
        '--color-primary-lighter': lighten(primary, 0.9),
        '--color-primary-light-20': alpha(light, 0.2),
        '--color-primary-light-26': alpha(light, 0.26),
        '--color-primary-light-40': alpha(light, 0.4),
        '--color-primary-light-50': alpha(light, 0.5),
    };
};

interface Props {
    children: ReactNode;
}

const AppThemeProvider = ({ children }: Props) => {
    const rawColor = useAppSelector(selectPrimaryColor);
    const labelTextColor = useAppSelector(selectLabelTextColor);
    const userLogo = useAppSelector(selectUserLogo);
    const primaryColor = safeColor(rawColor);

    const theme = useMemo(
        () => buildTheme(primaryColor, labelTextColor),
        [primaryColor, labelTextColor]
    );

    useEffect(() => {
        try {
            const root = document.documentElement;
            const vars = derivedVars(primaryColor);
            Object.entries(vars).forEach(([key, value]) => {
                root.style.setProperty(key, value);
            });
            root.style.setProperty('--color-label-text', labelTextColor);
        } catch {
            // màu từ API không hợp lệ — giữ nguyên CSS vars mặc định từ index.scss
        }
    }, [primaryColor, labelTextColor]);

    // Favicon theo brand: account thuộc group -> dùng logo_image; fallback -> icon cũ (logo.png).
    useEffect(() => {
        const link =
            document.querySelector<HTMLLinkElement>("link[rel='icon']");
        if (!link) return;
        link.href = userLogo || `${process.env.PUBLIC_URL || ''}/logo.png`;
    }, [userLogo]);

    return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};

export default AppThemeProvider;
